/**
 * Cloudflare Stream URL builders — the three shapes a surface actually asks for.
 *
 * ── It composes no host any more (T742 slice 3) ─────────────────────────────────
 *
 * Each function below is one call into the egress seam (`~/lib/domain/delivery-url`), which
 * is the only module allowed to choose a delivery host (ADR-0025 decision 1). It passes `{}`
 * as config on purpose: a Stream URL needs nothing from the environment, so the same call
 * works in a loader and inside `SlotVideo` rendering in a woman's browser. Before slice 3 the
 * seam was server-only and these three functions had to build the host themselves — the
 * second seam, which existed only because the first one could not be reached from a component.
 *
 * The named functions stay because the call sites read better for them, and because the two
 * paragraphs below are things a reader needs at the point of use.
 *
 * ── Why the customer-subdomain form is mandatory ────────────────────────────────
 *
 * CF Stream embeds MUST use `customer-<CODE>.cloudflarestream.com/<UID>/iframe`, NOT the
 * legacy generic `iframe.cloudflarestream.com/<uid>` — the generic form returns the in-player
 * "This content is blocked. Contact the site owner to fix the issue." page for this account
 * (verified 2026-06-14 against the first published hero video; the /watch URL on the customer
 * subdomain played fine while the generic iframe did not, in a clean incognito profile — so it
 * is not signed URLs, allowed origins, nor a browser extension). The code itself is a PUBLIC,
 * account-stable identifier declared in `~/config/external-origins`, which is also what builds
 * the CSP that has to allow this exact host.
 */
import { resolveDeliveryUrl } from "~/lib/domain/delivery-url";

/** Build the Cloudflare Stream iframe embed URL for a video uid. */
export function streamEmbedUrl(uid: string): string {
  return resolveDeliveryUrl({}, { kind: "stream-video", uid, form: "iframe" }, "in-page");
}

/**
 * Direct MP4 URL for a video uid — used as the `src` of a native `<video>` for decorative
 * background playback (muted/autoplay/loop, no player runtime), e.g. the home hero.
 *
 * REQUIRES "downloads" enabled on the Stream video (one-time per video; CF generates
 * `/downloads/default.mp4` asynchronously). Until enabled it 404s, in which case the
 * `<video poster>` (`streamPosterUrl`) keeps showing the still — so the surface degrades
 * gracefully rather than going black. The BO affordance to enable downloads on upload is
 * owned by T243.
 */
export function streamMp4Url(uid: string): string {
  return resolveDeliveryUrl({}, { kind: "stream-video", uid, form: "mp4" }, "in-page");
}

/**
 * Auto-generated poster (thumbnail) for a video uid — CF Stream serves this for every ready
 * video, no extra config. Used as the `<video poster>` so the hero shows a still immediately
 * (and before downloads is enabled). `time` picks the frame (e.g. "1s"); `height` caps the
 * rendered size.
 */
export function streamPosterUrl(
  uid: string,
  opts: { time?: string; height?: number } = {},
): string {
  return resolveDeliveryUrl(
    {},
    { kind: "stream-poster", uid, time: opts.time, height: opts.height },
    "in-page",
  );
}
